import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import React, { useEffect, useState } from "react";
import { useCalendarUrlListener } from "../../hooks/useCalendarUrlListener";
import { useDetectLogin } from "../../hooks/useDetectLogin";
import { useDetectLogout } from "../../hooks/useDetectLogout";
import { MonetizationProvider } from "../../hooks/useMonetization";
import { useSendAIExclusion } from "../../hooks/useSendAIExclusion";
import { useSendExperimentsAndFeatureFlags } from "../../hooks/useSendExperimentsAndFeatureFlags";
import { useSendInvitesToNonUser } from "../../hooks/useSendInvitesToNonUsers";
import { LaunchDarklyProvider } from "../../launch-darkly";
import { getCurrentOrg } from "../../util/org.util";
import { PostMessageManager } from "../../util/post-message.util";
import { IProps } from "./ChromeWrapperTypes";
import { DeprecatedChromeWrapper } from "./DeprecatedChromeWrapper.component";

const ChromeWrapper = (props: IProps) => {
  const { sendInvites, viewer, calendarUrl, chromeExtensionOpen, windowUserEmail } = props;
  const user = viewer?.user;
  const org = getCurrentOrg(viewer);
  const orgId = org?.id;
  const chromeExtensionFlagsErrorable = user?.chromeExtensionFlagsErrorable;
  const chromeExtensionFlags = getValue(chromeExtensionFlagsErrorable, "Flags");
  const userFlags = chromeExtensionFlags?.flags;

  const [authed, setAuthed] = useState(!!user);

  const onUserLoggedIn = () => {
    setAuthed(true);
    PostMessageManager.sendAuthenticated(true);
  };

  const onUserLoggedOut = () => {
    setAuthed(false);
    PostMessageManager.sendAuthenticated(false);
  };

  useEffect(() => {
    // Handle auth related matters here
    // Send `authed` to extension for initial load
    PostMessageManager.sendAuthenticated(authed);
    if (authed && userFlags) {
      PostMessageManager.sendUserFlags(userFlags);
    }
  }, []);

  useDetectLogin(user, org, onUserLoggedIn, calendarUrl);
  useDetectLogout(user, onUserLoggedOut);

  useCalendarUrlListener(calendarUrl, chromeExtensionOpen, windowUserEmail);
  useSendInvitesToNonUser(sendInvites, orgId);
  useSendAIExclusion();
  useSendExperimentsAndFeatureFlags(org);

  return (
    <MonetizationProvider orgId={orgId || null}>
      <DeprecatedChromeWrapper {...props} authed={authed} />{" "}
    </MonetizationProvider>
  );
};

const ProviderWrappedChromeWrapper = (props: IProps) => {
  const { viewer } = props;
  const user = viewer?.user;
  const org = getCurrentOrg(viewer);

  // We need LaunchDarklyProvider to be rendered before ChromeWrapper
  return (
    <LaunchDarklyProvider org={org} user={user} loading={false}>
      <ChromeWrapper {...props} />
    </LaunchDarklyProvider>
  );
};

export default ProviderWrappedChromeWrapper;
